import { FormPage } from 'backend';
import { MainContent, Template } from 'components';
import { FormikErrors } from 'formik';
import {
  HANDLED_SUBMISSION_RESULT_STATE_STATUS,
  useFormErrors,
  useHandleSubmissionResult,
  useSubmitInputData,
  useInitPage,
  useSubmissionState,
} from 'hooks';
import React, { useRef, useMemo } from 'react';
import { FormPageInputData, FORM_PAGE_TYPE } from 'shared';
import { scrollAndFocus } from 'utils';
import { ValidationErrors } from 'validators';
import { ReCaptchaGlobalStyles } from '../shared';
import { ConfirmContent, ConfirmContentProps } from './ConfirmContent';

const INTRO_ERROR_MESSAGE =
  'フォームの内容が変更された可能性があります。入力画面に戻って入力内容を変更してください。';

export const Confirm: React.FC = () => {
  const {
    currentFormPage,
    inputData,
    reCaptchaSetting,
    mainContentRef,
    authentication,
  } = useInitPage();
  const { formErrors, resetFormErrors, updateFormErrors } = useFormErrors();
  const { submit } = useSubmitInputData({
    reCaptchaSetting,
  });
  const { handleSubmissionResult } = useHandleSubmissionResult();
  const globalErrorRef = useRef<HTMLDivElement>(null);
  const { disabledSubmission, setDisabledControl } = useSubmissionState();

  const buttonDisabled = useMemo(() => {
    return {
      next: disabledSubmission,
      previous: disabledSubmission,
    };
  }, [disabledSubmission]);

  const handleSubmissionClick: ConfirmContentProps['onSubmissionClick'] =
    async ({ endpoint }) => {
      if (disabledSubmission) {
        return;
      }
      setDisabledControl(true);
      resetFormErrors();
      const result = await submit({
        endpoint,
        authentication,
      });
      const state = handleSubmissionResult(result);
      const {
        success,
        unauthorizedError,
        unrecoverableError,
        submissionError,
        fileUploadValidationError,
        globalError,
      } = HANDLED_SUBMISSION_RESULT_STATE_STATUS;
      if (state.status === fileUploadValidationError) {
        const { errorMessage, unrecoverable } = state;
        setDisabledControl(unrecoverable);
        updateFormErrors({
          global: [errorMessage],
        });
        scrollAndFocus(globalErrorRef.current);
        return;
      }

      setDisabledControl(false);

      if (
        state.status === success ||
        state.status === unrecoverableError ||
        state.status === unauthorizedError
      ) {
        return;
      }
      if (state.status === submissionError) {
        const { submission } = state.error;
        updateFormErrors({ submission });
        return;
      }
      if (state.status === globalError) {
        const { global } = state.error;
        updateFormErrors({ global });
        scrollAndFocus(globalErrorRef.current);
        return;
      }

      // validation error
      // - 確認画面にいる間にフォーム定義が変更されたり項目が削除された場合
      // - 確認画面にいる間に残席枠上限、期限でエラーが出る状況になった場合
      const { global, properties } = state.error;

      const propertyErrors = properties
        ? Object.values(properties as ValidationErrors)
            ?.filter((message) => message)
            .flat()
        : [];

      if (
        hasOnlyRegistrationLimitError(properties, currentFormPage?.properties)
      ) {
        updateFormErrors({
          global: [...(global ?? []), ...propertyErrors],
        });
        scrollAndFocus(globalErrorRef.current);
        return;
      }
      updateFormErrors({
        global: [INTRO_ERROR_MESSAGE, ...(global ?? []), ...propertyErrors],
      });
      scrollAndFocus(globalErrorRef.current);
    };
  const displayAlternateTextForReCaptcha = reCaptchaSetting?.hideBadge || false;

  if (!currentFormPage) {
    return null;
  }
  const { header, footer, title, message, design, pageId } = currentFormPage;

  return (
    <>
      <ReCaptchaGlobalStyles reCaptchaSetting={reCaptchaSetting} />
      <Template
        header={header}
        footer={footer}
        design={design}
        displayAlternateTextForReCaptcha={displayAlternateTextForReCaptcha}
      >
        <MainContent
          title={title}
          message={message}
          pageType={FORM_PAGE_TYPE.CONFIRM}
          pageId={pageId}
          design={design}
          ref={mainContentRef}
        >
          <ConfirmContent
            formPage={currentFormPage}
            inputData={inputData}
            buttonDisabled={buttonDisabled}
            onSubmissionClick={handleSubmissionClick}
            submissionErrors={formErrors.submission}
            globalErrors={formErrors.global}
            ref={globalErrorRef}
          />
        </MainContent>
      </Template>
    </>
  );
};

const hasOnlyRegistrationLimitError = (
  errors: FormikErrors<FormPageInputData> | undefined,
  formPageProperties: FormPage['properties'] | undefined,
) => {
  if (errors == null || formPageProperties == null) {
    return false;
  }
  const currentErrorMessages = Object.values(errors).flatMap(
    (itemErrors) => itemErrors,
  );
  const registrationLimitErrorMessages = Object.values(formPageProperties)
    .map((property: unknown) => (property as any)?.registrationLimit)
    .filter((errorMessages) => errorMessages != null)
    .flatMap((errorMessages) => Object.values(errorMessages));
  const hasErrorWithoutRegistrationLimit = currentErrorMessages.some(
    (error) => !registrationLimitErrorMessages.includes(error),
  );
  return !hasErrorWithoutRegistrationLimit;
};
